import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  DollarSign, Link2, TrendingUp, Users, CheckCircle2, ArrowRight,
  Mail, Zap, BarChart3, ChevronDown, Gift, Clock,
  Shield, Rocket, Trophy, Wallet,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import SEOHead from '@/components/SEOHead';
import { REFERRAL, REFERRAL_DISCOUNT, PAYOUT_MINIMUM_USD, percent, tierRanges } from '@/config/earnings';
import { priceListFor } from '@/config/pricing';

// ─── Data ─────────────────────────────────────────────────────────────────────

// From builderslab/shared/earnings.json — the same tiers the commission ledger
// applies (services/referral.js) and the Affiliate Terms describe. This page
// used to advertise four tiers at 25–40% with perks that did not exist, while
// the Terms said three at 20–40%.
const TIER_STYLE = [
  { icon: Zap, color: 'zinc', perks: ['Your own referral code and link', `Friends get ${REFERRAL_DISCOUNT} off any plan`, 'Live earnings in your dashboard'] },
  { icon: TrendingUp, color: 'orange', popular: true, perks: ['Everything in the tier before', 'A higher rate on every purchase', 'Payouts straight to your bank'] },
  { icon: Trophy, color: 'purple', perks: ['Everything in the tier before', 'Our highest rate', 'Priority partner support'] },
];
const COMMISSION_TIERS = tierRanges().map((t, i) => ({
  name: t.name,
  referrals: t.range,
  commission: percent(t.rate),
  ...TIER_STYLE[Math.min(i, TIER_STYLE.length - 1)],
}));

const HOW_IT_WORKS = [
  {
    step: '01',
    icon: Link2,
    title: 'Get Your Code',
    body: 'Every BuildersLab account has a referral code and link. Find them in your dashboard under Earn — no application needed.',
  },
  {
    step: '02',
    icon: Users,
    title: 'Share It',
    body: 'Post it on YouTube, Twitter, your blog, newsletter, or anywhere your audience hangs out.',
  },
  {
    step: '03',
    icon: TrendingUp,
    title: 'Friends Save',
    body: `Anyone who enters your code at checkout gets ${REFERRAL_DISCOUNT} off any plan — and you earn on what they pay.`,
  },
  {
    step: '04',
    icon: Wallet,
    title: 'Get Paid',
    body: `Commissions clear after ${REFERRAL.holdDays} days. Request a bank transfer once $${PAYOUT_MINIMUM_USD} is available.`,
  },
];

const FAQS = [
  {
    q: 'When do I get paid?',
    a: `Each commission is held for ${REFERRAL.holdDays} days (the chargeback window), then it is yours to withdraw. Once $${PAYOUT_MINIMUM_USD} is available, request a payout from Dashboard → Earn and we send it to your bank account by transfer, usually within a few working days.`,
  },
  {
    q: 'How are referrals tracked?',
    a: `By your code. Anyone who signs up through your link is linked to you, and your code is filled in for them automatically when they buy a plan. Anyone can also type your code at checkout — they get ${REFERRAL_DISCOUNT} off and you earn your tier's rate.`,
  },
  {
    q: 'Do I earn on renewals?',
    a: 'Yes. You earn your tier rate every time a customer pays for a plan with your code — and for customers you referred, your code is filled in at every checkout.',
  },
  {
    q: 'Can I promote if I\'m outside the US?',
    a: 'Absolutely. The programme is open worldwide. Commissions are booked in US dollars and paid by bank transfer.',
  },
  {
    q: 'Is there a cost to join?',
    a: 'Zero. The affiliate programme is completely free to join. You earn money — we never charge you a setup or membership fee.',
  },
  {
    q: 'What content is allowed?',
    a: 'Honest reviews, tutorials, comparisons, and demonstrations are all welcome. Spam, misleading claims, or paid search bidding on our brand name are not permitted.',
  },
];

const PROMO_CHANNELS = [
  { icon: '📹', label: 'YouTube Tutorials', desc: 'Walk through building a site in real time' },
  { icon: '🐦', label: 'Twitter / X Threads', desc: 'Share your generation results live' },
  { icon: '✍️', label: 'Blog & SEO Articles', desc: 'Write reviews, comparisons, how-tos' },
  { icon: '📧', label: 'Email Newsletters', desc: 'Drop a rec to your subscriber list' },
  { icon: '💬', label: 'Discord & Communities', desc: 'Share in dev, design, or freelance spaces' },
  { icon: '📸', label: 'Instagram & TikTok', desc: 'Show the magic in short-form video' },
];

const EMPTY_FORM = {
  name: '', email: '', website: '', audience: '', channel: '', message: '',
};

const CHANNEL_OPTIONS = [
  'Please select…',
  'YouTube',
  'Twitter / X',
  'Blog / Website',
  'Newsletter',
  'Instagram',
  'TikTok',
  'Discord / Slack communities',
  'LinkedIn',
  'Podcast',
  'Multiple channels',
  'Other',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const colorMap = {
  zinc:   { bg: 'bg-zinc-800/60',    border: 'border-zinc-700',      icon: 'text-zinc-400',   comm: 'text-white' },
  blue:   { bg: 'bg-blue-500/10',    border: 'border-blue-500/25',   icon: 'text-blue-400',   comm: 'text-blue-400' },
  orange: { bg: 'bg-orange-500/10',  border: 'border-orange-500/30', icon: 'text-orange-400', comm: 'text-orange-400' },
  purple: { bg: 'bg-purple-500/10',  border: 'border-purple-500/25', icon: 'text-purple-400', comm: 'text-purple-400' },
};

function InputField({ label, id, type = 'text', value, onChange, placeholder, required }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-zinc-300">
        {label}{required && <span className="text-orange-500 ml-1">*</span>}
      </label>
      <input
        id={id} type={type} value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder} required={required}
        className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/30 transition-all"
      />
    </div>
  );
}

function SelectField({ label, id, value, onChange, options, required }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-zinc-300">
        {label}{required && <span className="text-orange-500 ml-1">*</span>}
      </label>
      <div className="relative">
        <select
          id={id} value={value}
          onChange={e => onChange(e.target.value)}
          required={required}
          style={{ colorScheme: 'dark' }}
          className="w-full appearance-none bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/30 transition-all pr-10"
        >
          {options.map(opt => (
            <option key={opt} value={opt.startsWith('Please') ? '' : opt} className="bg-zinc-900">{opt}</option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
      </div>
    </div>
  );
}

// ─── Earnings Calculator ──────────────────────────────────────────────────────

function EarningsCalculator() {
  const [referrals, setReferrals] = useState(25);
  const [plan, setPlan] = useState('pro');

  // The list prices customers are charged (builderslab/shared/pricing.json),
  // less the referral discount they get with your code.
  const usdPlans = priceListFor('USD').plans;
  const planValues = Object.fromEntries(Object.entries(usdPlans).map(([id, p]) => [id, p.monthly * (1 - REFERRAL.discountRate)]));
  const planLabels = {
    starter: `Starter ($${usdPlans.starter.monthly}/mo)`,
    pro: `Pro ($${usdPlans.pro.monthly}/mo)`,
    agency: `Studio ($${usdPlans.agency.monthly}/mo)`,
  };

  const getTier = (n) => {
    const tiers = [...REFERRAL.tiers].sort((a, b) => a.minCustomers - b.minCustomers);
    return tiers.reduce((cur, t) => (n >= t.minCustomers ? t : cur), tiers[0]);
  };

  const tier = getTier(referrals);
  const monthlyEarnings = Math.round(referrals * planValues[plan] * tier.rate);
  const yearlyEarnings = monthlyEarnings * 12;

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-8">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-orange-500/15 border border-orange-500/25 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-orange-400" />
        </div>
        <div>
          <h3 className="text-white font-bold text-lg">Earnings Calculator</h3>
          <p className="text-zinc-500 text-sm">Estimate your monthly recurring income</p>
        </div>
      </div>

      {/* Referrals slider */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-3">
          <label className="text-sm font-medium text-zinc-300">Active Referrals</label>
          <span className="text-white font-bold text-lg">{referrals}</span>
        </div>
        <input
          type="range" min="1" max="500" value={referrals}
          onChange={e => setReferrals(Number(e.target.value))}
          className="w-full h-2 bg-zinc-800 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-orange-500 [&::-webkit-slider-thumb]:shadow-[0_0_10px_rgba(249,115,22,0.5)]"
        />
        <div className="flex justify-between text-xs text-zinc-600 mt-1">
          <span>1</span>
          <span>500+</span>
        </div>
      </div>

      {/* Plan selector */}
      <div className="mb-8">
        <label className="text-sm font-medium text-zinc-300 block mb-3">Average Plan Referred</label>
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(planLabels).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setPlan(id)}
              className={`rounded-xl py-2.5 px-2 text-xs font-semibold border transition-all ${
                plan === id
                  ? 'bg-orange-500/20 border-orange-500/50 text-orange-400'
                  : 'bg-zinc-900 border-zinc-800 text-zinc-500 hover:border-zinc-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Result */}
      <div className="bg-zinc-900/80 border border-white/8 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-1">
          <span className="text-zinc-400 text-sm">Your commission tier</span>
          <span className="text-orange-400 font-bold text-sm">{tier.name} — {Math.round(tier.rate * 100)}%</span>
        </div>
        <div className="h-px bg-white/5 my-4" />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-zinc-500 text-xs mb-1">Monthly Recurring</div>
            <div className="text-3xl font-extrabold text-white">${monthlyEarnings.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-zinc-500 text-xs mb-1">Annual Recurring</div>
            <div className="text-3xl font-extrabold bg-gradient-to-r from-orange-400 to-orange-500 bg-clip-text text-transparent">
              ${yearlyEarnings.toLocaleString()}
            </div>
          </div>
        </div>
        <p className="text-zinc-600 text-xs mt-4">
          * Assumes every customer pays monthly with your code ({REFERRAL_DISCOUNT} off). Actual earnings vary.
        </p>
      </div>
    </div>
  );
}

// ─── FAQ Item ─────────────────────────────────────────────────────────────────

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/8 rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-white/[0.02] transition-colors"
      >
        <span className="text-white font-medium text-sm pr-4">{q}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <div
            key="body"
           
           
           
           
            className="overflow-hidden"
          >
            <div className="px-6 pb-5 text-zinc-400 text-sm leading-relaxed border-t border-white/5 pt-4">
              {a}
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Affiliate() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const API_URL = import.meta.env.VITE_API_URL ?? '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${API_URL}/api/affiliates/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:     form.name,
          email:    form.email,
          website:  form.website,
          audience: form.audience,
          channel:  form.channel,
          message:  form.message,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) { setStatus('success'); setForm(EMPTY_FORM); }
      else throw new Error(data.error || 'Submission failed');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
      setTimeout(() => setStatus('idle'), 6000);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <SEOHead
        title="Affiliate Programme — Earn Up to 40% Commission"
        description="Earn up to 40% recurring commission by referring customers to BuildersLab. Join 1,400+ affiliates already earning monthly."
        ogUrl="https://builderslab.net/Affiliate"
      />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative pt-36 pb-28 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-orange-500/7 blur-[140px] rounded-full pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[400px] bg-emerald-500/5 blur-[120px] rounded-full pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 text-sm text-orange-400 font-medium mb-6">
              <DollarSign className="w-4 h-4" />
              Affiliate Programme
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight mb-6">
              Your Audience Is{' '}
              <span className="bg-gradient-to-r from-orange-400 to-emerald-400 bg-clip-text text-transparent">
                Worth More
              </span>
            </h1>

            <p className="text-xl text-zinc-400 leading-relaxed max-w-2xl mx-auto mb-4">
              Share your code. Your friends get <span className="text-white font-bold">{REFERRAL_DISCOUNT} off</span> any
              plan, and you earn up to <span className="text-white font-bold">{percent(Math.max(...REFERRAL.tiers.map(t => t.rate)))} commission</span> on
              what they pay — every time they pay with your code.
            </p>

            <p className="text-zinc-500 text-base mb-10">
              Join 1,400+ creators, developers, and marketers already earning with BuildersLab.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/dashboard?section=earn"
                className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-semibold shadow-[0_0_30px_rgba(249,115,22,0.35)] hover:shadow-[0_0_40px_rgba(249,115,22,0.5)] transition-all group"
              >
                Get Your Referral Code
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="#calculator"
                className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-white font-medium transition-all"
              >
                <BarChart3 className="w-4 h-4" />
                Calculate Your Earnings
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Quick facts ──────────────────────────────────────────────────── */}
      <section className="py-12 border-y border-white/5 bg-white/[0.01]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: 'Up to 40%', label: 'Recurring commission' },
              { value: REFERRAL_DISCOUNT, label: 'Off for your friends' },
              { value: `$${PAYOUT_MINIMUM_USD}`, label: 'Minimum payout' },
              { value: '$0', label: 'To join' },
            ].map((s, i) => (
              <div
                key={s.label}
               
               
               
               
              >
                <div className="text-2xl sm:text-3xl font-extrabold text-white mb-1">{s.value}</div>
                <div className="text-sm text-zinc-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Commission Tiers ─────────────────────────────────────────────── */}
      <section className="py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div
           
           
           
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
              Earn More as You Grow
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Our tiered structure rewards your performance. The more customers you bring in,
              the higher your commission rate — automatically.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5">
            {COMMISSION_TIERS.map((tier, i) => {
              const c = colorMap[tier.color];
              return (
                <div
                  key={tier.name}
                 
                 
                 
                 
                  className={`relative flex flex-col ${c.bg} border ${c.border} rounded-2xl p-6`}
                >
                  {tier.popular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="flex items-center gap-1 px-3 py-1 bg-orange-500 text-ink text-[11px] font-bold rounded-full uppercase tracking-widest shadow-[0_4px_12px_rgba(249,115,22,0.5)]">
                        <Zap className="w-2.5 h-2.5" />
                        Most Earned
                      </span>
                    </div>
                  )}

                  <div className={`w-10 h-10 rounded-xl ${c.bg} border ${c.border} flex items-center justify-center mb-4`}>
                    <tier.icon className={`w-5 h-5 ${c.icon}`} />
                  </div>

                  <h3 className="text-white font-bold text-lg mb-0.5">{tier.name}</h3>
                  <p className="text-zinc-500 text-xs mb-3">{tier.referrals} paying customers</p>

                  <div className={`text-4xl font-extrabold ${c.comm} mb-5`}>{tier.commission}</div>

                  <ul className="space-y-2.5 flex-1">
                    {tier.perks.map(perk => (
                      <li key={perk} className="flex items-start gap-2.5 text-sm text-zinc-400">
                        <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${c.icon}`} />
                        {perk}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────────────────── */}
      <section className="py-24 border-y border-white/5 bg-white/[0.015]">
        <div className="max-w-5xl mx-auto px-6">
          <div
           
           
           
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
              Start Earning in 4 Steps
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {HOW_IT_WORKS.map((step, i) => (
              <div
                key={step.step}
               
               
               
               
                className="flex flex-col items-center text-center bg-white/[0.03] border border-white/8 rounded-2xl p-6 hover:border-orange-500/25 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mb-4">
                  <step.icon className="w-5 h-5 text-orange-400" />
                </div>
                <div className="text-orange-500 font-extrabold text-xs tracking-widest mb-2">{step.step}</div>
                <h3 className="text-white font-bold mb-2">{step.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Earnings Calculator ──────────────────────────────────────────── */}
      <section id="calculator" className="py-28">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div
             
             
             
             
            >
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-5">
                What Could You Earn?
              </h2>
              <p className="text-zinc-400 text-lg leading-relaxed mb-8">
                You earn a share of what your referrals pay, every time they pay for a plan with
                your code. The calculator on the right gives you a realistic picture.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: Clock, text: 'Friends you refer stay linked to you — your code is filled in at their every checkout' },
                  { icon: Shield, text: `Paid to your bank account from $${PAYOUT_MINIMUM_USD}, after a ${REFERRAL.holdDays}-day chargeback hold` },
                  { icon: Gift, text: 'Your code is your first name and a number — easy to remember and to share' },
                  { icon: Rocket, text: 'Tier upgrades are automatic — we track your referrals for you' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3.5">
                    <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-orange-400" />
                    </div>
                    <p className="text-zinc-400 text-sm leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>

              {/* Promo channels */}
              <div className="bg-white/[0.02] border border-white/8 rounded-2xl p-5">
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-4">Where top affiliates promote</p>
                <div className="grid grid-cols-2 gap-3">
                  {PROMO_CHANNELS.map(ch => (
                    <div key={ch.label} className="flex items-center gap-2.5">
                      <span className="text-lg">{ch.icon}</span>
                      <div>
                        <div className="text-white text-xs font-medium">{ch.label}</div>
                        <div className="text-zinc-600 text-[11px]">{ch.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div
             
             
             
             
            >
              <EarningsCalculator />
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="py-24 border-y border-white/5 bg-white/[0.015]">
        <div className="max-w-3xl mx-auto px-6">
          <div
           
           
           
            className="text-center mb-14"
          >
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Application Form ─────────────────────────────────────────────── */}
      <section id="apply" className="py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(249,115,22,0.05)_0%,transparent_65%)] pointer-events-none" />
        <div className="relative max-w-2xl mx-auto px-6">
          <div
           
           
           
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 text-sm text-orange-400 font-medium mb-5">
              <DollarSign className="w-3.5 h-3.5" />
              Affiliate Application
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4 tracking-tight">
              Apply to the Programme
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              Applications take under 2 minutes. You'll receive your unique link within 24 hours.
            </p>
          </div>

          <AnimatePresence mode="wait">
            {status === 'success' ? (
              <div
                key="success"
               
               
               
                className="bg-emerald-500/10 border border-emerald-500/25 rounded-3xl p-12 text-center"
              >
                <div className="w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">You're In!</h3>
                <p className="text-zinc-400 leading-relaxed max-w-sm mx-auto">
                  Your application has been received. Check your inbox within 24 hours for
                  your affiliate link, dashboard access, and onboarding guide.
                </p>
                <div className="mt-8 flex items-center justify-center gap-2 text-sm text-zinc-500">
                  <Mail className="w-4 h-4" />
                  <span>Confirmation sent to your email</span>
                </div>
              </div>
            ) : (
              <div key="form">
                <div className="relative">
                  <div className="absolute -inset-[1px] rounded-3xl bg-gradient-to-br from-orange-500/20 via-transparent to-emerald-500/10 blur-sm" />
                  <div className="relative bg-zinc-950 border border-white/10 rounded-3xl p-8 sm:p-10 shadow-2xl">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <InputField label="Full Name" id="name" value={form.name} onChange={set('name')} placeholder="Alex Johnson" required />
                        <InputField label="Email Address" id="email" type="email" value={form.email} onChange={set('email')} placeholder="alex@channel.com" required />
                      </div>
                      <InputField
                        label="Your Website / Channel / Profile URL"
                        id="website" type="url" value={form.website}
                        onChange={set('website')} placeholder="https://youtube.com/@yourhandle"
                      />
                      <div className="grid sm:grid-cols-2 gap-4">
                        <InputField
                          label="Audience Size"
                          id="audience" value={form.audience}
                          onChange={set('audience')} placeholder="e.g. 12,000 subscribers"
                          required
                        />
                        <SelectField
                          label="Primary Channel"
                          id="channel" value={form.channel}
                          onChange={set('channel')} options={CHANNEL_OPTIONS}
                          required
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label htmlFor="message" className="block text-sm font-medium text-zinc-300">
                          How will you promote BuildersLab?
                        </label>
                        <textarea
                          id="message" value={form.message}
                          onChange={e => set('message')(e.target.value)}
                          rows={4}
                          placeholder="Tell us about your audience, content style, and how you'd present BuildersLab to them…"
                          className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/30 transition-all resize-none"
                        />
                      </div>

                      <AnimatePresence>
                        {status === 'error' && (
                          <p
                           
                           
                           
                            className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3"
                          >
                            {errorMsg}
                          </p>
                        )}
                      </AnimatePresence>

                      <Button
                        type="submit"
                        disabled={status === 'loading'}
                        className="w-full h-13 py-3.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-semibold rounded-xl shadow-[0_0_20px_rgba(249,115,22,0.25)] hover:shadow-[0_0_30px_rgba(249,115,22,0.4)] transition-all disabled:opacity-60 disabled:cursor-not-allowed group text-base"
                      >
                        {status === 'loading' ? (
                          <span className="flex items-center justify-center gap-2">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Submitting…
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            Join the Affiliate Programme
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </span>
                        )}
                      </Button>

                      <p className="text-center text-xs text-zinc-600">
                        Free to join. Instant approval for qualifying creators.
                        No minimum threshold for payouts.
                      </p>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ── Bottom CTA ───────────────────────────────────────────────────── */}
      <section className="border-t border-white/5 py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-zinc-500 text-sm mb-2">Questions about the programme?</p>
          <a
            href="mailto:affiliates@builderslab.net"
            className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold transition-colors text-lg"
          >
            <Mail className="w-5 h-5" />
            affiliates@builderslab.net
          </a>
        </div>
      </section>

    </div>
  );
}
